import { z } from 'zod';
/**
 * The canonical roles (SPEC.md §2.2). Role is a single flat enum — there is no
 * separate department axis on the *role*, though department is stored on User (D22).
 *
 * Three tiers of authority (SUPER_ADMIN > ADMIN > SUPERVISOR) and eight lateral
 * roles beneath them. See `ROLE_INHERITS` for what that ordering actually means, and
 * decisions D4, D23 and D24 for how the list grew.
 *
 * The per-action permission matrix is still open (SPEC.md §9.2) beyond the ticket
 * transitions encoded in ./lifecycle/ticket-lifecycle.
 */
export const UserRole = {
    /**
     * Root tier (decision D23). Distinguished from ADMIN by what it may destroy: staff
     * accounts including other admins, room topology, and the system audit trail.
     *
     * Deliberately not offered by the ordinary admin surface — an ADMIN who could mint
     * a SUPER_ADMIN would make the tier decorative.
     */
    SuperAdmin: 'SUPER_ADMIN',
    Admin: 'ADMIN',
    /**
     * Shift duty manager / HOD (decision D24). The tier SPEC.md §2.2 predicted would be
     * needed: it oversees a shift without holding full platform authority, so a GM and a
     * duty manager are finally distinguishable.
     *
     * Not an admin. `hasAdminAuthority` refuses it, so it reaches no `@Roles(ADMIN)`
     * route and none of the admin portal.
     */
    Supervisor: 'SUPERVISOR',
    FrontDesk: 'FRONT_DESK',
    Housekeeping: 'HOUSEKEEPING',
    Maintenance: 'MAINTENANCE',
    /**
     * Multi-skilled technician (decision D24). Distinct from MAINTENANCE, which stays
     * the engineering-specific role; both sit in the MAINTENANCE department and both
     * can claim its work.
     */
    Technician: 'TECHNICIAN',
    IT: 'IT',
    /** General hotel staff with no departmental specialism (decision D24). */
    Staff: 'STAFF',
    /** Read-oriented compliance role (decision D24). Holds no authority tier. */
    Auditor: 'AUDITOR',
    ExternalVendor: 'EXTERNAL_VENDOR',
};
export const userRoleSchema = z.enum([
    UserRole.SuperAdmin,
    UserRole.Admin,
    UserRole.Supervisor,
    UserRole.FrontDesk,
    UserRole.Housekeeping,
    UserRole.Maintenance,
    UserRole.Technician,
    UserRole.IT,
    UserRole.Staff,
    UserRole.Auditor,
    UserRole.ExternalVendor,
]);
/**
 * Roles the ordinary admin surface may assign.
 *
 * SUPER_ADMIN is withheld: promotion to root is a super-admin action, and an ADMIN
 * able to grant it could escalate themselves through a second account in two steps.
 *
 * SUPERVISOR *is* assignable. It confers no admin authority, so granting it is not a
 * step towards root — an admin appointing a duty manager is ordinary staff
 * administration, and routing it through the root tier would make that tier a
 * bottleneck for a nightly task.
 */
export const assignableByAdminSchema = z.enum([
    UserRole.Admin,
    UserRole.Supervisor,
    UserRole.FrontDesk,
    UserRole.Housekeeping,
    UserRole.Maintenance,
    UserRole.Technician,
    UserRole.IT,
    UserRole.Staff,
    UserRole.Auditor,
    UserRole.ExternalVendor,
]);
/**
 * Roles each role subsumes *directly* (decisions D23, D24).
 *
 * Strictly one-directional and deliberately shallow — each entry names only the tier
 * immediately below it, and `roleSatisfies` walks the chain. Stating the closure here
 * instead (SUPER_ADMIN → [ADMIN, SUPERVISOR]) would mean every future tier had to be
 * added to every entry above it, and the one that got missed would be a silent hole.
 *
 * An explicit map rather than a numeric rank, because the eight roles below SUPERVISOR
 * are lateral to each other: Housekeeping is not "below" Maintenance, and a rank would
 * invent an ordering the domain does not have.
 */
const ROLE_INHERITS = {
    [UserRole.SuperAdmin]: [UserRole.Admin],
    [UserRole.Admin]: [UserRole.Supervisor],
};
/**
 * Whether `actual` meets a requirement, directly or by inheritance.
 *
 * Lives in shared because both sides must agree. The server enforces it; the client
 * decides what to render. When they disagreed, a super-admin was redirected away from
 * a page the API would happily have served — permissions the UI refused to show.
 *
 * Transitive: SUPER_ADMIN inherits ADMIN, which inherits SUPERVISOR, so root satisfies
 * a supervisor requirement without SUPERVISOR appearing in its own entry. The `seen`
 * set makes a cycle in the map terminate rather than hang — an authority check is the
 * wrong place to trust that a constant was edited correctly.
 */
export function roleSatisfies(actual, required) {
    if (required.includes(actual))
        return true;
    const seen = new Set([actual]);
    const queue = [...(ROLE_INHERITS[actual] ?? [])];
    while (queue.length > 0) {
        const role = queue.shift();
        if (seen.has(role))
            continue;
        seen.add(role);
        if (required.includes(role))
            return true;
        queue.push(...(ROLE_INHERITS[role] ?? []));
    }
    return false;
}
/**
 * Shorthand for the common case: may this role use the admin surface at all?
 *
 * Takes a raw string because callers hold a role that came off the wire. Anything
 * unrecognised — an older client, a truncated session — answers false rather than
 * throwing: an authority check should fail closed.
 *
 * SUPERVISOR answers false. It is an authority tier but not an administrative one, and
 * this question gates the whole `/admin` surface.
 */
export function hasAdminAuthority(role) {
    const parsed = userRoleSchema.safeParse(role);
    return parsed.success && roleSatisfies(parsed.data, [UserRole.Admin]);
}
/**
 * Is this the root tier, and nothing less?
 *
 * The narrow counterpart to `hasAdminAuthority`, added when four surfaces — the equipment
 * register, property configuration, staff administration and parts spending — moved from
 * ADMIN to SUPER_ADMIN. An ADMIN keeps the `/admin` route and answers false here.
 *
 * Expressed through `roleSatisfies` rather than `role === SUPER_ADMIN` on purpose. The
 * server refuses these surfaces with `@Roles(UserRole.SuperAdmin)`, which the guard
 * resolves through the very same function, so writing the client's half a different way
 * would be two implementations of one question — the drift this module exists to prevent.
 * Nothing inherits SUPER_ADMIN today, so the two agree; if anything ever did, they would
 * still agree.
 *
 * Fails closed on anything unrecognised, exactly as its siblings do.
 */
export function isSuperAdmin(role) {
    const parsed = userRoleSchema.safeParse(role);
    return parsed.success && roleSatisfies(parsed.data, [UserRole.SuperAdmin]);
}
/**
 * May this role act as a shift supervisor (decision D24)?
 *
 * True for SUPERVISOR and, by inheritance, for ADMIN and SUPER_ADMIN — a manager can
 * always do what a duty manager can. False for every lateral role, and fails closed on
 * anything unrecognised, exactly as `hasAdminAuthority` does.
 */
export function hasSupervisorAuthority(role) {
    const parsed = userRoleSchema.safeParse(role);
    return parsed.success && roleSatisfies(parsed.data, [UserRole.Supervisor]);
}
/**
 * Is this person the manager of that department (decision D45)?
 *
 * The conjunction of two columns that already exist: a supervisory role, seated in the
 * department in question. There is deliberately no third source of truth — no
 * `is_manager` boolean, no manager pointer on the department — because either would be
 * a second copy of what role and seat already say, and copies drift.
 *
 * This makes `User.department` load-bearing on a manager account, not merely a filter:
 * the maintenance manager receives Maintenance's alerts *because* they are seated in
 * MAINTENANCE (`sendToDepartment` reads the field, D22), and a manager seated elsewhere
 * manages, by this predicate, only where they sit. Production was verified in exactly
 * that shape on 2026-09-01 — each ADMIN seated in the department they run.
 *
 * Documentation-in-code as much as a routing tool: nothing needs to call this for R5 to
 * hold, since the department broadcast already reaches a correctly seated manager. It
 * exists so the convention has a name, and so anything that later wants "the manager of
 * X" asks this one question instead of inventing a competing answer.
 *
 * Fails closed on anything unrecognised, exactly as its siblings do.
 */
export function overseesDepartment(role, userDepartment, target) {
    return hasSupervisorAuthority(role) && userDepartment === target;
}
/**
 * May this role put a named person on a job — at creation, or at any point after
 * (decisions D34, D38)?
 *
 * The distinction the roles turn on is not seniority, it is dispatch. Reporting stays
 * open to everyone — SPEC.md §5.1.1 has a ticket created by "FRONT_DESK, any staff, or
 * QR", and a housekeeper who finds a broken tap and cannot say so is the radio call this
 * product exists to replace. Naming who will do the work is a different act: it commits
 * someone else's shift, and it takes the job out of the department pool before anyone
 * has had the chance to claim it (D31).
 *
 * ONE predicate for both acts, because there is one write. Dispatch and reassignment set
 * the same column and leave the same audit shape; "was there a previous assignee" is a
 * property of the row, not of the caller's intent. This replaces `mayDispatchWork`, which
 * asked the narrower half of the same question — two predicates would be two places to
 * keep consistent, which is exactly how the verifier rule drifted (D36).
 *
 * SUPERVISOR is in the set and was not in `mayDispatchWork`. A duty manager creating a job
 * at 2am should be able to name who does it (D38).
 *
 * FRONT_DESK is included and is not an authority tier — it is the desk that fields the
 * guest and already routes work by hand, which is exactly the dispatch case. So this
 * cannot be expressed as `roleSatisfies` alone: the set is not a prefix of the hierarchy.
 *
 * Fails closed on anything unrecognised, as the other authority checks do.
 */
export function mayAssignWork(role) {
    const parsed = userRoleSchema.safeParse(role);
    if (!parsed.success)
        return false;
    return parsed.data === UserRole.FrontDesk || hasSupervisorAuthority(parsed.data);
}
/**
 * Ticket lifecycle states (SPEC.md §5.1.1).
 *
 * The traffic light in §5.1 is a *display* concern derived from these — see
 * `ticketStatusColour`. Storing the colour as the status cannot represent ESCALATED,
 * ON_HOLD or CLOSED, so the state is authoritative and the colour follows.
 *
 * The tail of the lifecycle is three states, not one:
 *
 *   RESOLVED = technician has completed the work; awaiting manager verification
 *   VERIFIED = manager has verified the work; awaiting administrative closure
 *   CLOSED   = closed
 *
 * RESOLVED keeps the meaning it always had — "the technician says it is done", never
 * "signed off" — so VERIFIED renames nothing and no stored row changes meaning.
 */
export const TicketStatus = {
    Open: 'OPEN',
    Escalated: 'ESCALATED',
    InProgress: 'IN_PROGRESS',
    OnHold: 'ON_HOLD',
    Resolved: 'RESOLVED',
    Closed: 'CLOSED',
    /**
     * Called off without being done (D27). Distinct from RESOLVED, which asserts the fault
     * was fixed, and from CLOSED, which follows it — a duplicate report or a job raised in
     * error was never fixed, and recording it as though it was corrupts every "how much did
     * we resolve" figure in §5.6. Reachable only from the root tier.
     */
    Cancelled: 'CANCELLED',
    /**
     * A manager has verified the completed work; administrative closure has not happened
     * yet. The step between RESOLVED and CLOSED, which used to be a single act by one
     * person — "verify and close" — and so could not distinguish a job someone actually
     * looked at from one that was tidied off the board.
     */
    Verified: 'VERIFIED',
};
export const ticketStatusSchema = z.enum([
    TicketStatus.Open,
    TicketStatus.Escalated,
    TicketStatus.InProgress,
    TicketStatus.OnHold,
    TicketStatus.Resolved,
    TicketStatus.Closed,
    TicketStatus.Cancelled,
    TicketStatus.Verified,
]);
/** Display colours for the traffic-light UI. */
export const TicketColour = {
    Red: 'RED',
    Yellow: 'YELLOW',
    Green: 'GREEN',
    Blue: 'BLUE',
    White: 'WHITE',
};
/**
 * ESCALATED shares red with OPEN — it is still an unstarted fault. The UI flags it
 * additionally (🚨) off the status itself, so the palette stays five colours.
 */
export const ticketStatusColour = {
    [TicketStatus.Open]: TicketColour.Red,
    [TicketStatus.Escalated]: TicketColour.Red,
    [TicketStatus.InProgress]: TicketColour.Yellow,
    [TicketStatus.OnHold]: TicketColour.Blue,
    [TicketStatus.Resolved]: TicketColour.Green,
    [TicketStatus.Closed]: TicketColour.White,
    // White with CLOSED, not green: the palette says "finished", and only green claims the
    // fault was actually fixed. A cancelled job must never read as a success on a wallboard.
    [TicketStatus.Cancelled]: TicketColour.White,
    // Green with RESOLVED, not white: green asserts the fault was actually fixed, which is
    // still true of a verified job — more so, since someone checked. White is reserved for
    // "finished", and VERIFIED is not finished; closure has not happened yet.
    [TicketStatus.Verified]: TicketColour.Green,
};
/** i18n key for each status, so no status label is ever hardcoded in the UI. */
export const ticketStatusLabelKey = {
    [TicketStatus.Open]: 'ticket.status.open',
    [TicketStatus.Escalated]: 'ticket.status.escalated',
    [TicketStatus.InProgress]: 'ticket.status.inProgress',
    [TicketStatus.OnHold]: 'ticket.status.onHold',
    [TicketStatus.Resolved]: 'ticket.status.resolved',
    [TicketStatus.Closed]: 'ticket.status.closed',
    [TicketStatus.Cancelled]: 'ticket.status.cancelled',
    [TicketStatus.Verified]: 'ticket.status.verified',
};
/**
 * Ticket priority (SPEC.md §5.1.2). Drives both SLA deadlines and the escalation
 * timer — see ./sla/sla-policy.
 */
export const TicketPriority = {
    Urgent: 'URGENT',
    High: 'HIGH',
    Medium: 'MEDIUM',
    Low: 'LOW',
};
export const ticketPrioritySchema = z.enum([
    TicketPriority.Urgent,
    TicketPriority.High,
    TicketPriority.Medium,
    TicketPriority.Low,
]);
/** Most to least urgent. Useful for sorting a queue without a lookup table. */
export const TICKET_PRIORITY_ORDER = [
    TicketPriority.Urgent,
    TicketPriority.High,
    TicketPriority.Medium,
    TicketPriority.Low,
];
export const ticketPriorityLabelKey = {
    [TicketPriority.Urgent]: 'ticket.priority.urgent',
    [TicketPriority.High]: 'ticket.priority.high',
    [TicketPriority.Medium]: 'ticket.priority.medium',
    [TicketPriority.Low]: 'ticket.priority.low',
};
/** Equipment classes (SPEC.md §5.2). OTHER keeps new plant from needing a migration. */
export const AssetCategory = {
    Hvac: 'HVAC',
    Boiler: 'BOILER',
    Ptac: 'PTAC',
    KitchenEquipment: 'KITCHEN_EQUIPMENT',
    Elevator: 'ELEVATOR',
    Other: 'OTHER',
};
export const assetCategorySchema = z.enum([
    AssetCategory.Hvac,
    AssetCategory.Boiler,
    AssetCategory.Ptac,
    AssetCategory.KitchenEquipment,
    AssetCategory.Elevator,
    AssetCategory.Other,
]);
export const assetCategoryLabelKey = {
    [AssetCategory.Hvac]: 'asset.category.hvac',
    [AssetCategory.Boiler]: 'asset.category.boiler',
    [AssetCategory.Ptac]: 'asset.category.ptac',
    [AssetCategory.KitchenEquipment]: 'asset.category.kitchenEquipment',
    [AssetCategory.Elevator]: 'asset.category.elevator',
    [AssetCategory.Other]: 'asset.category.other',
};
/** Photo-first capture plus voice notes (SPEC.md §5.1). */
export const AttachmentKind = {
    Photo: 'PHOTO',
    Voice: 'VOICE',
};
export const attachmentKindSchema = z.enum([AttachmentKind.Photo, AttachmentKind.Voice]);
/**
 * Which moment in a job's life a piece of media was captured at:
 *
 *   ISSUE        = at the report, showing the fault
 *   COMPLETION   = at hand-back, showing the work done
 *   VERIFICATION = at sign-off, showing what the manager checked
 *
 * Optional at every stage and never a gate. Decision D2 keeps proof a quality metric
 * rather than a precondition, so a stage with no photo simply has none.
 *
 * A different axis from `AttachmentKind`, which says what the media *is* rather than
 * when it was taken — a voice note recorded at sign-off is kind VOICE, stage
 * VERIFICATION. Optional on the record too: a null stage means "not a lifecycle photo"
 * (chat media, checklist-step proof), which is why nothing here carries a default.
 */
export const AttachmentStage = {
    Issue: 'ISSUE',
    Completion: 'COMPLETION',
    Verification: 'VERIFICATION',
};
export const attachmentStageSchema = z.enum([
    AttachmentStage.Issue,
    AttachmentStage.Completion,
    AttachmentStage.Verification,
]);
export const attachmentStageLabelKey = {
    [AttachmentStage.Issue]: 'attachment.stage.issue',
    [AttachmentStage.Completion]: 'attachment.stage.completion',
    [AttachmentStage.Verification]: 'attachment.stage.verification',
};
/** Shift swap approval flow (SPEC.md §5.5). */
export const ShiftSwapStatus = {
    Pending: 'PENDING',
    Approved: 'APPROVED',
    Rejected: 'REJECTED',
    Cancelled: 'CANCELLED',
};
export const shiftSwapStatusSchema = z.enum([
    ShiftSwapStatus.Pending,
    ShiftSwapStatus.Approved,
    ShiftSwapStatus.Rejected,
    ShiftSwapStatus.Cancelled,
]);
export const shiftSwapStatusLabelKey = {
    [ShiftSwapStatus.Pending]: 'shift.swap.pending',
    [ShiftSwapStatus.Approved]: 'shift.swap.approved',
    [ShiftSwapStatus.Rejected]: 'shift.swap.rejected',
    [ShiftSwapStatus.Cancelled]: 'shift.swap.cancelled',
};
/**
 * Which team owns a piece of work, and which team a person belongs to (SPEC.md §5.4).
 *
 * Under decision D22 this is a real axis on `User` as well as on work. The first
 * three mirror the frontline roles for historical reasons; the rest have no role of
 * their own, which is the whole point — an account is placed on a team by its
 * department, not by inference from what it is permitted to do.
 */
export const Department = {
    Housekeeping: 'HOUSEKEEPING',
    Maintenance: 'MAINTENANCE',
    IT: 'IT',
    FrontOffice: 'FRONT_OFFICE',
    FoodBeverage: 'FOOD_BEVERAGE',
    Security: 'SECURITY',
    Administration: 'ADMINISTRATION',
};
export const departmentSchema = z.enum([
    Department.Housekeeping,
    Department.Maintenance,
    Department.IT,
    Department.FrontOffice,
    Department.FoodBeverage,
    Department.Security,
    Department.Administration,
]);
export const departmentLabelKey = {
    [Department.Housekeeping]: 'department.housekeeping',
    [Department.Maintenance]: 'department.maintenance',
    [Department.IT]: 'department.it',
    [Department.FrontOffice]: 'department.frontOffice',
    [Department.FoodBeverage]: 'department.foodBeverage',
    [Department.Security]: 'department.security',
    [Department.Administration]: 'department.administration',
};
/** How a PPM schedule decides its next due date (SPEC.md §5.4). */
export const IntervalType = {
    Daily: 'DAILY',
    Weekly: 'WEEKLY',
    Monthly: 'MONTHLY',
    Quarterly: 'QUARTERLY',
    Yearly: 'YEARLY',
    MeterBased: 'METER_BASED',
};
export const intervalTypeSchema = z.enum([
    IntervalType.Daily,
    IntervalType.Weekly,
    IntervalType.Monthly,
    IntervalType.Quarterly,
    IntervalType.Yearly,
    IntervalType.MeterBased,
]);
export const intervalTypeLabelKey = {
    [IntervalType.Daily]: 'ppm.interval.daily',
    [IntervalType.Weekly]: 'ppm.interval.weekly',
    [IntervalType.Monthly]: 'ppm.interval.monthly',
    [IntervalType.Quarterly]: 'ppm.interval.quarterly',
    [IntervalType.Yearly]: 'ppm.interval.yearly',
    [IntervalType.MeterBased]: 'ppm.interval.meterBased',
};
/** METER_BASED is driven by a reading, so date arithmetic does not apply to it. */
export function isDateBasedInterval(interval) {
    return interval !== IntervalType.MeterBased;
}
/**
 * The department a role most likely belongs to, or null where there is no obvious
 * answer.
 *
 * No longer authoritative. Under D22 a user's department is stored and may disagree
 * with this entirely — an ADMIN can belong to MAINTENANCE. This survives only as the
 * value the admin form pre-selects when a role is picked, so the common case is one
 * tap; it must never be read as a substitute for the stored field.
 */
export const suggestedDepartmentForRole = {
    [UserRole.SuperAdmin]: null,
    [UserRole.Admin]: null,
    // A duty manager covers whichever department is short-staffed that night, so there
    // is no one answer — same reasoning as ADMIN above.
    [UserRole.Supervisor]: null,
    [UserRole.FrontDesk]: null,
    [UserRole.ExternalVendor]: null,
    // General staff and auditors are both cross-departmental by definition.
    [UserRole.Staff]: null,
    [UserRole.Auditor]: null,
    [UserRole.Housekeeping]: Department.Housekeeping,
    [UserRole.Maintenance]: Department.Maintenance,
    // Distinct from MAINTENANCE as a role, but the same team's work (D24), so the form
    // pre-selects the same department.
    [UserRole.Technician]: Department.Maintenance,
    [UserRole.IT]: Department.IT,
};
/**
 * Who receives escalations. There is no MANAGER role — decision D4 folded Duty
 * Manager / HOD into ADMIN, so that is the escalation target.
 */
export const ESCALATION_ROLE = UserRole.Admin;
/** A ticket in one of these states is still consuming operational attention. */
export const ACTIVE_TICKET_STATUSES = [
    TicketStatus.Open,
    TicketStatus.Escalated,
    TicketStatus.InProgress,
    TicketStatus.OnHold,
];
/**
 * Nothing follows these. CANCELLED joins CLOSED (D27): a job called off is finished with,
 * and the point of the state is that it did not end in a fix — reopening it would leave
 * the audit trail claiming work resumed on something nobody ever resumed.
 */
export function isTerminalStatus(status) {
    return status === TicketStatus.Closed || status === TicketStatus.Cancelled;
}
//# sourceMappingURL=enums.js.map